import {
  type FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';

import { constrainValue } from '../../helper.js';
import { TEXT_SPIN_BUTTON_DEFAULTS } from '../constants.js';
import type { TextSpinButtonProps } from '../types.js';

/** @internal */
export const useValueNavigation = (props: TextSpinButtonProps) => {
  // Set default keyboard behavior if undefined.
  const {
    options,
    onArrowUp = (idx, e) => {
      e.preventDefault();
      return idx - TEXT_SPIN_BUTTON_DEFAULTS.SMALL_STEP;
    },
    onArrowDown = (idx, e) => {
      e.preventDefault();
      return idx + TEXT_SPIN_BUTTON_DEFAULTS.SMALL_STEP;
    },
    onPageUp = (idx, e) => {
      e.preventDefault();
      return idx - TEXT_SPIN_BUTTON_DEFAULTS.BIG_STEP;
    },
    onPageDown = (idx, e) => {
      e.preventDefault();
      return idx + TEXT_SPIN_BUTTON_DEFAULTS.BIG_STEP;
    },
    onHome = (_, e) => {
      e.preventDefault();
      return 0;
    },
    onEnd = (_, e) => {
      e.preventDefault();
      return options.length - 1;
    },
    disabled,
    readOnly,
    defaultValue = TEXT_SPIN_BUTTON_DEFAULTS.VALUE,
    value: valueProp,
    onChange,
    isCyclic,
  } = props;

  const [value, setValue] = useState(valueProp ?? defaultValue);

  // Update value to the result of the given transform function.
  const handleValueNavigation = useCallback(
    (
      transform: (curIndex: number, e: FormEvent) => number,
      event: FormEvent,
    ) => {
      const sanitized = (idx: number) => {
        const maxIndex = options.length - 1;
        return constrainValue(idx, 0, maxIndex, isCyclic) ?? 0;
      };

      // if nothing was selected, start with the first configured value
      const curIndex =
        value === null ? 0 : options.indexOf(value || valueProp || '');
      const newIndex = sanitized(transform(curIndex, event));
      const newValue = options[newIndex];

      if (value !== newValue) {
        setValue(newValue);
        onChange?.(newValue);
      }
    },
    [value, options, valueProp, onChange, isCyclic],
  );

  // Lookup table for faster search
  const lookupTable = useMemo(
    () => options.map((v) => v[0].toLowerCase()),
    [options],
  );

  // For a given key, scan lookup table and update the value accordingly.
  const searchValue = useCallback(
    (key: string, fireOnChange = false) => {
      const idx = lookupTable.indexOf(key.toLowerCase());
      if (idx < 0) {
        return undefined;
      }

      const newValue = options[idx];
      if (value !== newValue) {
        setValue(newValue);
      }

      fireOnChange && onChange?.(newValue);

      return newValue;
    },
    [lookupTable, options, value, onChange],
  );

  const clearValue = useCallback(
    (fireOnChange = false) => {
      setValue(null);
      fireOnChange && onChange?.(null);
    },
    [onChange],
  );

  const onKeyDownHandler = useCallback(
    (e: ReactKeyboardEvent) => {
      if (disabled || readOnly) {
        // Prevent any value changes via keys, but don't prevent the default behavior.
        return;
      }

      const navigationHandlers = {
        ArrowUp: onArrowUp,
        ArrowDown: onArrowDown,
        PageUp: onPageUp,
        PageDown: onPageDown,
        Home: onHome,
        End: onEnd,
      };

      const navHandler =
        navigationHandlers[e.key as keyof typeof navigationHandlers];
      if (navHandler) {
        if (!e.shiftKey && e.key !== 'Shift') {
          handleValueNavigation(navHandler, e);
        }
        return;
      }

      switch (e.key) {
        case 'Backspace':
        case 'Delete':
          if (value !== null) {
            setValue(null);
            onChange?.(null);
          }
          e.preventDefault();
          break;
        default:
          // If a character key was pressed, look for a value starting with the char.
          if (!e.metaKey && !e.ctrlKey && e.key.match(/^\w$/)) {
            const newValue = searchValue(e.key);
            if (newValue !== undefined && newValue !== value) {
              onChange?.(newValue);
            }
            e.preventDefault();
          }
          break;
      }
    },
    [
      disabled,
      handleValueNavigation,
      onArrowDown,
      onArrowUp,
      onChange,
      onEnd,
      onHome,
      onPageDown,
      onPageUp,
      readOnly,
      searchValue,
      setValue,
      value,
    ],
  );

  useEffect(() => {
    // update value if component is in controlled mode
    if (valueProp !== undefined) {
      setValue(valueProp);
    }
  }, [setValue, valueProp]);

  return { value, onKeyDownHandler, searchValue, clearValue };
};
